import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabase from './supabaseClient';
// slugify imported via navigate to listing page

const ForSale = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [filterGenus, setFilterGenus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [genera, setGenera] = useState([]);
  const isMobile = window.innerWidth <= 768;

  const [verifiedListings, setVerifiedListings] = useState(new Set());
  const [messageModal, setMessageModal] = useState(null);
  const [msgForm, setMsgForm] = useState({ buyer_name: '', buyer_email: '', message: '' });
  const [msgSending, setMsgSending] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [msgError, setMsgError] = useState('');

  useEffect(() => {
    const fetchListings = async () => {
      const { data } = await supabase
        .from('inventory')
        .select('*, sellers(business_name, location_city, location_state, palmstreet_url)')
        .eq('is_public', true)
        .eq('status', 'available')
        .order('date_listed', { ascending: false });
      const all = data || [];
      setListings(all);
      const uniqueGenera = [...new Set(all.map(l => l.genus).filter(Boolean))].sort();
      setGenera(uniqueGenera);
      setLoading(false);

      // Compute True Plant Value badges per listing
      const computeBadges = async (listingData) => {
        if (!listingData || listingData.length === 0) return;
        const verified = new Set();
        const groups = {};
        listingData.forEach(l => {
          const key = `${l.common_name}__${l.sale_type||''}__${l.sale_size||''}`;
          if (!groups[key]) groups[key] = { name: l.common_name, type: l.sale_type, size: l.sale_size, ids: [] };
          groups[key].ids.push({ id: l.id, price: l.price });
        });
        for (const group of Object.values(groups)) {
          let q = supabase.from('plants').select('sold_price').eq('status', 'approved').ilike('common_name', group.name);
          if (group.type) q = q.ilike('sale_type', group.type);
          if (group.size) q = q.eq('sale_size', String(group.size));
          const { data: pd } = await q.limit(50);
          if (pd && pd.length >= 3) {
            const nums = pd.map(p => parseFloat(String(p.sold_price).replace(/[^0-9.]/g,''))).filter(n => !isNaN(n) && n > 0);
            if (nums.length >= 3) {
              const avg = nums.reduce((a,b)=>a+b,0)/nums.length;
              group.ids.forEach(({ id, price }) => {
                const lp = parseFloat(String(price||'').replace(/[^0-9.]/g,''));
                if (!isNaN(lp) && lp > 0 && Math.abs(lp - avg) / avg <= 0.25) verified.add(id);
              });
            }
          }
        }
        setVerifiedListings(new Set(verified));
      };
      computeBadges(all);


    };
    fetchListings();
  }, []);



  const filtered = listings.filter(l => {
    if (search && !l.common_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterGenus && l.genus !== filterGenus) return false;
    if (filterType && l.sale_type !== filterType) return false;
    return true;
  });

  const formatPrice = (val) => {
    if (!val) return '—';
    const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? val : '$' + num.toFixed(2);
  };

  const sendMessage = async () => {
    if (!msgForm.buyer_name || !msgForm.buyer_email || !msgForm.message) {
      setMsgError('Please fill in all fields.'); return;
    }
    setMsgSending(true); setMsgError('');
    const { error } = await supabase.from('messages').insert({
      listing_id: messageModal.id,
      seller_id: messageModal.seller_id,
      buyer_name: msgForm.buyer_name,
      buyer_email: msgForm.buyer_email,
      message: msgForm.message,
    });
    if (error) { setMsgError('Failed to send. Please try again.'); console.error(error); }
    else { setMsgSent(true); }
    setMsgSending(false);
  };

  return (
    <>
    <div style={{ paddingBottom: '80px' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #1e3d20 0%, #2c4a2e 60%, #4a7c59 100%)', padding: isMobile ? '48px 24px' : '60px 24px', textAlign: 'center', borderRadius: '0 0 40px 40px', marginBottom: '40px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '6px', color: '#8fb99a', textTransform: 'uppercase', marginBottom: '16px', fontFamily: 'Lato, sans-serif' }}>🌿 Member Listings</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '36px' : '54px', color: '#f5f0e8', fontWeight: 700, marginBottom: '16px', lineHeight: 1.1 }}>Plants for Sale</h1>
        <p style={{ color: 'rgba(245,240,232,0.8)', fontFamily: 'Lato, sans-serif', fontSize: '16px', maxWidth: '480px', margin: '0 auto 32px', lineHeight: 1.7 }}>
          Browse rare plants listed by our community members. Click any listing to contact the seller.
        </p>
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plant name..."
            style={{ width: '100%', padding: '16px 24px', border: 'none', borderRadius: '14px', fontSize: '16px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', outline: 'none', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          />
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
          <select value={filterGenus} onChange={(e) => setFilterGenus(e.target.value)}
            style={{ padding: '10px 14px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', background: '#fff' }}>
            <option value="">All Genera</option>
            {genera.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
            style={{ padding: '10px 14px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', background: '#fff' }}>
            <option value="">All Types</option>
            <option value="Potted">Potted</option>
            <option value="Cutting">Cutting</option>
            <option value="Plug/Starter">Plug/Starter</option>
            <option value="Corm">Corm</option>
            <option value="Tissue Culture (TC)">Tissue Culture (TC)</option>
          </select>
          {(filterGenus || filterType || search) && (
            <button onClick={() => { setFilterGenus(''); setFilterType(''); setSearch(''); }}
              style={{ padding: '10px 14px', background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Clear
            </button>
          )}
          <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginLeft: 'auto' }}>
            <strong>{filtered.length}</strong> listing{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontStyle: 'italic', padding: '60px' }}>Loading listings...</p>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.08)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌿</div>
            <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif', fontSize: '16px', marginBottom: '8px' }}>No listings found.</p>
            <p style={{ color: '#8b9e8c', fontFamily: 'Lato, sans-serif', fontSize: '13px' }}>Try adjusting your search or check back soon — members add new plants regularly.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
            {filtered.map((listing) => (
              <div key={listing.id} onClick={() => navigate(`/listing/${listing.id}/${listing.sale_type ? listing.sale_type.toLowerCase().replace(/[^a-z0-9]+/g,'-') : ''}${listing.sale_size ? '-'+listing.sale_size+'in' : ''}-${(listing.common_name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`)} style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(44,74,46,0.1)', border: '1px solid rgba(143,185,154,0.3)', display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(44,74,46,0.18)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(44,74,46,0.1)'; }}>
                <div style={{ position: 'relative' }}>
                  {listing.image_url ? (
                    <img src={listing.image_url} alt={listing.common_name} style={{ width: '100%', height: isMobile ? '140px' : '200px', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ height: isMobile ? '140px' : '200px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>🌿</div>
                  )}
                  {verifiedListings.has(listing.id) && (
                    <img src="/trueplantvaluebadge.png" alt="Verified True Plant Value" style={{ position: 'absolute', top: '8px', right: '8px', width: isMobile ? '52px' : '64px', height: isMobile ? '52px' : '64px', pointerEvents: 'none' }} />
                  )}
                </div>
                <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '10px', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                    {[listing.genus, listing.sale_type, listing.sale_size ? `${listing.sale_size}"` : null].filter(Boolean).join(' · ')}
                  </div>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '15px' : '18px', color: '#1e3d20', marginBottom: '8px', lineHeight: 1.3 }}>{listing.common_name}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <div style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '20px' : '24px', color: '#2c4a2e', fontWeight: 700 }}>
                      {formatPrice(listing.price)}
                    </div>

                  </div>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {listing.variegation_level && <span style={{ fontSize: '10px', background: '#fdf8ff', color: '#7c4a7c', padding: '2px 7px', borderRadius: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>{listing.variegation_level} var.</span>}
                    {listing.quantity > 1 && <span style={{ fontSize: '10px', background: '#f7f3ec', color: '#8b6914', padding: '2px 7px', borderRadius: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Qty: {listing.quantity}</span>}
                  </div>
                  {listing.notes && <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a5e4b', marginBottom: '10px', lineHeight: 1.5 }}>{listing.notes}</p>}
                  <div style={{ fontSize: '11px', color: '#8b9e8c', fontFamily: 'Lato, sans-serif', marginBottom: '12px' }}>
                    {listing.sellers?.business_name && <span>{listing.sellers.business_name}</span>}
                    {(listing.location || listing.sellers?.location_city) && (
                      <span> · {listing.location || [listing.sellers?.location_city, listing.sellers?.location_state].filter(Boolean).join(', ')}</span>
                    )}
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#8b9e8c', marginBottom: '12px' }}>Are you a member with plants to sell?</p>
            <button onClick={() => navigate('/seller/login')} style={{ background: 'transparent', border: '2px solid #4a7c59', color: '#4a7c59', borderRadius: '24px', padding: '12px 32px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              List Your Plants →
            </button>
          </div>
        )}
      </div>
    </div>

      {/* Message Modal */}
      {messageModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setMessageModal(null); }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '28px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1e3d20', marginBottom: '6px' }}>Contact Seller</h3>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '20px' }}>Re: <strong>{messageModal.common_name}</strong> — {(() => { const n = parseFloat(String(messageModal.price).replace(/[^0-9.]/g,'')); return isNaN(n) ? messageModal.price : '$'+n.toFixed(2); })()}</p>
            {msgSent ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '8px' }}>Message Sent!</p>
                <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '20px' }}>The seller will reply to your email address.</p>
                <button onClick={() => setMessageModal(null)} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', padding: '10px 24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Close</button>
              </div>
            ) : (
              <>
                {[{ label: 'Your Name', key: 'buyer_name', placeholder: 'e.g. Jane Smith' }, { label: 'Your Email', key: 'buyer_email', placeholder: 'your@email.com' }].map(({ label, key, placeholder }) => (
                  <div key={key} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '5px', fontFamily: 'Lato, sans-serif' }}>{label}</label>
                    <input value={msgForm[key]} onChange={(e) => setMsgForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                      style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #c4a882', borderRadius: '10px', fontSize: '14px', fontFamily: 'Lato, sans-serif', boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                ))}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '5px', fontFamily: 'Lato, sans-serif' }}>Message</label>
                  <textarea value={msgForm.message} onChange={(e) => setMsgForm(f => ({ ...f, message: e.target.value }))} placeholder="Hi, I'm interested in this plant. Is it still available?" rows={4}
                    style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #c4a882', borderRadius: '10px', fontSize: '14px', fontFamily: 'Lato, sans-serif', boxSizing: 'border-box', resize: 'vertical', outline: 'none' }} />
                </div>
                {msgError && <p style={{ color: '#c0392b', fontFamily: 'Lato, sans-serif', fontSize: '13px', marginBottom: '12px' }}>{msgError}</p>}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={sendMessage} disabled={msgSending}
                    style={{ flex: 1, padding: '12px', background: msgSending ? '#8fb99a' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 700, cursor: msgSending ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif' }}>
                    {msgSending ? 'Sending...' : '💬 Send Message'}
                  </button>
                  <button onClick={() => setMessageModal(null)} style={{ padding: '12px 20px', background: 'transparent', border: '1.5px solid #c4a882', color: '#4a5e4b', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ForSale;